import { TemporaryPermissionStatus } from "@/api-schema-enums";
import { useTemporaryPermissionCreateMutation } from "@/common/api/temporary-permission/use-temporary-permission-create.mutation";
import { getStartOfToday } from "@/common/helpers/date-input";
import { formatLocalDateToUtcIso } from "@/common/helpers/format-date";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Form } from "@/components/ui/form";
import { userStore } from "@/store/user-store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  TemporaryPermissionFormFields,
  TemporaryPermissionFormSchema,
  TemporaryPermissionFormSchemaType,
} from "./_components/create-form-fields";

export function CreateView() {
  const { t } = useTranslation(["ram", "common"]);
  const pageTitle = t("ram:RequestTemporaryPermissions");
  const pageSubtitle = t("common:CompleteRequiredInformationDescription");
  const [_, navigate] = useLocation();
  const { user } = userStore();
  const { mutateAsync, isPending } = useTemporaryPermissionCreateMutation();

  const defaultValues = useMemo(() => {
    const validFrom = getStartOfToday();
    const validUntil = new Date(validFrom);
    validUntil.setDate(validUntil.getDate() + 7);
    return {
      request_for: "",
      reason: "",
      valid_from: validFrom,
      valid_until: validUntil,
    };
  }, []);

  const form = useForm<TemporaryPermissionFormSchemaType>({
    resolver: zodResolver(TemporaryPermissionFormSchema),
    defaultValues,
  });

  const onSubmit = async (values: TemporaryPermissionFormSchemaType) => {
    if (!user?.uuid) {
      throw new Error("User UUID is required");
    }
    if (values.request_for === user.uuid) {
      form.setError("request_for", { message: t("ram:CannotRequestYourOwnPermissions") });
      return;
    }

    await toastMutationStatus({
      mutationFn: () =>
        mutateAsync({
          request_for: values.request_for,
          reason: values.reason,
          valid_from: formatLocalDateToUtcIso(values.valid_from),
          valid_until: formatLocalDateToUtcIso(values.valid_until),
          request_by: user.uuid,
          status: TemporaryPermissionStatus.Pending,
        }),
      successMessage: t("ram:RequestHasBeenCreated"),
      errorMessage: t("ram:ErrorCreatingRequest"),
    });

    navigate("~/ram/temporary/requests");
  };

  return (
    <CenteredLayout variant={CenteredLayoutVariant.Form} title={pageTitle} subTitle={pageSubtitle}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <TemporaryPermissionFormFields
            formControl={form.control}
            isLoading={isPending}
            currentUserUuid={user?.uuid}
          />
        </form>
      </Form>
    </CenteredLayout>
  );
}
